import React, { useEffect, useRef, useState } from 'react';
import { Trophy, Award, Globe, Users, ArrowUpRight, Star } from 'lucide-react';

const Achievements = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="achievements"
      ref={sectionRef}
      className="relative py-20 px-6 overflow-hidden"
      style={{
        background: 'var(--bg-primary)',
        minHeight: '80vh', // Slightly compact height
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-0 left-1/2 w-[600px] h-[400px] opacity-10"
          style={{
            background: 'radial-gradient(circle, #FFD700 0%, transparent 70%)', // Gold glow for achievements
            filter: 'blur(100px)',
            transform: 'translate(-50%, -50%)'
          }}
        />
      </div>

      <div className="max-w-5xl mx-auto w-full relative z-10">
        {/* Section Title */}
        <div className={`text-center mb-12 ${isVisible ? 'animate-fade-down' : 'opacity-0'}`}>
          <span className="text-xs font-bold tracking-[0.3em] uppercase text-brand-primary mb-3 block">
            Recognition
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--text-primary)]">
            Awards & <span style={{ color: '#FFD700' }}>Honors</span>
          </h2>
          <div className="w-16 h-1 mx-auto bg-gradient-to-r from-transparent via-[#FFD700] to-transparent opacity-50" />
        </div>

        {/* Main Hero Card - IEEE Winner */}
        <div 
          className={`relative group rounded-2xl overflow-hidden mb-8 border border-[var(--border-subtle)] bg-[var(--bg-overlay)] backdrop-blur-sm transition-all duration-300 hover:border-[#FFD700]/50 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.2s' }}
        >
          <div className="grid md:grid-cols-5 gap-0">
            {/* Image Section */}
            <div className="md:col-span-2 relative h-64 md:h-auto overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/80 via-transparent to-transparent z-10" />
              <img 
                src="/ieee-winner.jpg" 
                alt="Hackathon Winner" 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* Winner Badge */}
              <div className="absolute top-4 left-4 z-20 bg-[#FFD700] text-black text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-[0_0_20px_rgba(255,215,0,0.4)]">
                <Trophy size={14} fill="black" />
                #1 WINNER
              </div>
            </div>

            {/* Content Section */}
            <div className="md:col-span-3 p-6 md:p-10 flex flex-col justify-center">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2 group-hover:text-[#FFD700] transition-colors">
                    IEEE Hackathon 2025
                  </h3>
                  <p className="text-[#FFD700] font-medium text-sm flex items-center gap-2">
                    <Star size={14} fill="#FFD700" />
                    VIBEFORGE Coding Challenge
                  </p>
                </div>
                <ArrowUpRight className="text-[var(--text-muted)] group-hover:text-[var(--text-primary)] transition-colors" size={24} />
              </div>
              
              <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-6 border-l-2 border-[var(--border-subtle)] pl-4">
                Secured 1st place among 100+ teams in a high-intensity coding marathon. 
                Architected a real-time AI solution that solved complex optimization problems, 
                demonstrating speed, accuracy, and innovative thinking.
              </p>

              <div className="flex flex-wrap gap-3">
                {['Problem Solving', 'Innovation', 'Real-time Coding'].map((tag, i) => (
                  <span 
                    key={i} 
                    className="text-xs px-3 py-1 rounded-full bg-[var(--bg-overlay)] border border-[var(--border-subtle)] text-[var(--text-secondary)]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Achievements Grid */}
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              icon: Globe,
              title: 'CAMEL-AI Ambassador',
              desc: 'Official Global Representative',
              color: '#00D9FF'
            },
            {
              icon: Award,
              title: 'Freelance AI Developer',
              desc: 'End-to-end AI app delivery',
              color: '#00FF88'
            },
            {
              icon: Users,
              title: 'Problem Solving',
              desc: '200+ DSA problems solved',
              color: '#FF00FF'
            }
          ].map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className={`p-5 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-overlay)] hover:bg-[var(--bg-overlay)]/80 transition-all duration-300 hover:-translate-y-1 group ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
                style={{ animationDelay: `${0.3 + index * 0.1}s` }}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="p-3 rounded-lg bg-[var(--bg-overlay)] group-hover:scale-110 transition-transform duration-300"
                    style={{ color: item.color }}
                  >
                    <Icon size={24} />
                  </div>
                  <div>
                    <h4 className="text-[var(--text-primary)] font-bold text-sm mb-1">{item.title}</h4>
                    <p className="text-[var(--text-muted)] text-xs">{item.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .animate-fade-down { animation: fadeDown 0.8s ease-out forwards; }
        .animate-fade-up { animation: fadeUp 0.8s ease-out forwards; }

        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default Achievements;